import './styles.scss';
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import track from 'react-tracking';
import { Chip } from '@edx/paragon';
import { CloseSmall } from '@edx/paragon/icons';
import { useDispatch } from 'react-redux';
import useBreakpointMatch from '@prospectus/common/hooks/useBreakpointMatch';
import { FormattedMessage, injectIntl } from 'gatsby-plugin-react-intl';
import { intlShape } from '@prospectus/common/types/intlShape';
import { submitEvent } from '@utils/analytics';
import { appendProgramToProgramType } from '@utils/edxProductDisplay';
import { PRODUCT_TYPES, QUIZ_MATCH_PARAM } from '@utils/search';
import { WebviewContext, SearchPageContext } from '@utils/context';
import useSearchParams from '@prospectus/common/hooks/useSearchParams';
import { toggleOption } from '@data/actions/filters';
import messages from '../../messages';
import ClearRefinements from '../ClearRefinements';

function FacetChip(props) {
  const {
    iconAfter, onIconAfterClick, label, srOnlyLabel,
  } = props;
  return (
    <div className="facet-chip">
      <Chip
        iconAfter={iconAfter}
        onIconAfterClick={onIconAfterClick}
        className="bg-white border border-light-300 rounded rounded-3 mx-0"
      >
        <span className={classNames(
          'x-small text-primary-700',
        )}
        >{label}
        </span>
        { srOnlyLabel && (
        <span className="sr-only">
          {srOnlyLabel}
        </span>
        )}
      </Chip>
    </div>
  );
}
function CurrentRefinements(props) {
  const {
    intl, refinements,
  } = props;

  const isMobile = useBreakpointMatch('medium');
  const [showAll, toggleShowAll] = useState(!isMobile);
  const isWebview = useContext(WebviewContext);
  const { resultType, pageType } = useContext(SearchPageContext);

  const { searchParams, removeSearchParam, clearSearch } = useSearchParams();
  const dispatch = useDispatch();

  const sendAnalyticsEvent = (attribute, label) => {
    const componentProps = {
      category: 'search',
      label,
    };
    submitEvent(props.tracking, componentProps, `edx.bi.user.${attribute}.removed`);
  };

  const clearRefinement = (event, { attribute, label }) => {
    sendAnalyticsEvent(attribute, label);
    event.preventDefault();
    removeSearchParam(attribute, label);
    // Update vertical filters state
    dispatch(toggleOption({ category: attribute, option: label }));
  };

  const getLabel = ({ attribute, label }) => {
    if (attribute === 'program_type') {
      return appendProgramToProgramType(label);
    }
    return label;
  };

  const getQuizMatchRecommendationsLabel = () => (
    <span>
      <FormattedMessage
        id="prospectus.quiz.match.personalized.recommendations"
        description="Personalized recommendations refinement"
        defaultMessage="Personalized recommendations"
      />
    </span>
  );

  const getQuizMatchWarningMessage = () => (
    <span>
      <FormattedMessage
        id="prospectus.quiz.match.warning.message"
        description="Personalized recommendations refinement clear warning"
        defaultMessage="Please note that removing “Personalized recommendations” filter will take you to the general search page and your recommendations will be lost."
      />
    </span>
  );

  const clearQuizMatchRefinement = (event) => {
    event.preventDefault();
    clearSearch();
  };

  // convert { language: ['English', 'Spanish', ...] , level: ['Introductory'], ...}
  // to [
  //  {attribute: 'language', label: 'English'},
  //  {attribute: 'language', label: 'Spanish'},
  //  {attribute: 'level', label: 'Introductory'},
  //  ...
  //  ]
  const flattenRefinements = () => {
    const refinementsAsList = Object.entries(refinements).map(
      ([attributeName, currentSelections]) => currentSelections.map(
        (selectionLabel) => ({ attribute: attributeName, label: selectionLabel }),
      ),
    );
    return refinementsAsList.reduce((allRefinements, singleAttributeRefinements) => (
      [
        ...allRefinements,
        ...singleAttributeRefinements,
      ]
    ), []);
  };

  const refinementList = flattenRefinements();
  const hasLearningTypeRefinement = PRODUCT_TYPES.includes(resultType);
  const includeLearningTypeInCount = hasLearningTypeRefinement && !isWebview;

  const totalAppliedFilters = refinementList.length;

  const showLimit = includeLearningTypeInCount ? 2 : 3;

  const withinShowLimit = totalAppliedFilters > showLimit;
  const lastRefinement = refinementList.length > 0 ? refinementList[totalAppliedFilters - 1] : {};

  if (pageType === 'shop-page') {
    return null;
  }

  if (searchParams.has(QUIZ_MATCH_PARAM)) {
    return (
      <div className="current-refinements mt-3">
        <ul className="list-inline d-flex flex-wrap mb-0 align-items-center">
          <li
            className={classNames(
              'list-inline-item d-flex align-items-center h-100 mr-3',
            )}
          >
            <FacetChip
              iconAfter={CloseSmall}
              onIconAfterClick={clearQuizMatchRefinement}
              label={getQuizMatchRecommendationsLabel()}
            />
          </li>
          <li className="list-inline-item d-flex align-items-center h-100">
            <ClearRefinements />
          </li>
        </ul>
        <p className="x-small mb-0 mt-1 text-danger-500">{getQuizMatchWarningMessage()}</p>
      </div>
    );
  }
  return (
    <div className="current-refinements mt-3">
      <ul className="list-inline d-flex flex-wrap mb-0 align-items-center">
        { includeLearningTypeInCount && (
          <li
            className={classNames(
              'list-inline-item d-flex align-items-center h-100',
              { 'mr-3': (!!lastRefinement.label || showAll) && isMobile },
              { 'mr-1': !lastRefinement.label && !showAll },
            )}
            key={resultType}
          >
            <FacetChip
              iconAfter={CloseSmall}
              onIconAfterClick={() => removeSearchParam('tab')}
              label={resultType
                && messages[`prospectus.search.new.filters.learning_type.${resultType}`]
                && intl.formatMessage(messages[`prospectus.search.new.filters.learning_type.${resultType}`])}
              srOnlyLabel={resultType
                && messages[`prospectus.search.new.facets.clear-learning-type.${resultType}`]
                && intl.formatMessage(messages[`prospectus.search.new.facets.clear-learning-type.${resultType}`])}
            />
          </li>
        )}
        {refinementList.filter((i, index) => (showAll ? index > -1 : index < showLimit))
          .map(item => (
            <li
              className={classNames(
                'list-inline-item d-flex align-items-center h-100',
                { 'mr-3': (item.label !== lastRefinement.label || showAll) && isMobile },
                { 'mr-1': item.label === lastRefinement.label },
              )}
              key={item.label}
            >
              <FacetChip
                iconAfter={CloseSmall}
                onIconAfterClick={event => clearRefinement(event, item)}
                label={getLabel(item)}
                srOnlyLabel={intl.formatMessage(
                  messages['prospectus.search.new.facets.clear-one'],
                  {
                    facet: item.attribute,
                    label: item.label,
                  },
                )}
              />
            </li>
          ))}

        { withinShowLimit && isMobile && (
        <li className="list-inline-item d-flex align-items-center mr-0 h-100">
          <button
            className="btn btn-light badge badge-light badge-facet m-0 px-3 py-0"
            onClick={() => toggleShowAll(!showAll)}
            type="button"
          >
            {showAll ? intl.formatMessage(messages['prospectus.search.new.facets.showless'])
              : `+${refinementList.length - showLimit}`}
          </button>
        </li>
        )}
        { (totalAppliedFilters > 0 || includeLearningTypeInCount)
        && (
          <li className="list-inline-item d-flex align-items-center h-100">
            <ClearRefinements />
          </li>
        )}
      </ul>
    </div>
  );
}

CurrentRefinements.propTypes = {
  refinements: PropTypes.shape({}),
  tracking: PropTypes.shape().isRequired,
  intl: intlShape.isRequired,
};

FacetChip.propTypes = {
  iconAfter: PropTypes.node.isRequired,
  onIconAfterClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  srOnlyLabel: PropTypes.string,
};

FacetChip.defaultProps = {
  srOnlyLabel: '',
};

CurrentRefinements.defaultProps = {
  refinements: {},
};

export default track({
  component: 'current-refinements',
})(injectIntl(CurrentRefinements));
